import type { SquadPlayer, PlayerPoolEntry } from '../types/database';
export type { PlayerPoolEntry };

/**
 * Points banked when a player is removed from a slot.
 * Formula: (player.points − player.pointsAtJoining) × multipliers[slotIndex]
 */
export function bankTennisPlayerContribution(
  player: SquadPlayer,
  multipliers: number[]
): number {
  const delta = player.points - (player.pointsAtJoining ?? 0);
  const mult  = multipliers[player.slotIndex ?? 0] ?? 1;
  return delta * mult;
}

/**
 * Returns the banked contribution for BOTH players in a position swap,
 * using each player's ORIGINAL slot multiplier.
 */
export function bankTennisPositionSwap(
  playerA: SquadPlayer,
  playerB: SquadPlayer,
  multipliers: number[]
): { bankedA: number; bankedB: number; totalBanked: number } {
  const bankedA = bankTennisPlayerContribution(playerA, multipliers);
  const bankedB = bankTennisPlayerContribution(playerB, multipliers);
  return { bankedA, bankedB, totalBanked: bankedA + bankedB };
}

/**
 * Apply a player swap: replace the player at `slotIndex` with `incomingPlayer`.
 * Banks the outgoing player's contribution and resets the incoming player's
 * pointsAtJoining to their current points snapshot.
 * Returns a new players array (no mutation) + the banked points delta.
 */
export function applyTennisPlayerSwap(params: {
  players: SquadPlayer[];
  gender: 'men' | 'women';
  slotIndex: number;
  incomingPlayer: PlayerPoolEntry;
  multipliers: number[];
}): { updatedPlayers: SquadPlayer[]; bankedDelta: number } {
  const { players, gender, slotIndex, incomingPlayer, multipliers } = params;

  const outgoing = players.find(p => p.gender === gender && p.slotIndex === slotIndex);
  const bankedDelta = outgoing ? bankTennisPlayerContribution(outgoing, multipliers) : 0;

  const newPlayer: SquadPlayer = {
    playerId: incomingPlayer.playerId,
    playerName: incomingPlayer.name,
    team: incomingPlayer.team,
    role: gender,
    gender,
    points: incomingPlayer.points,
    pointsAtJoining: incomingPlayer.points,
    slotIndex,
    matchPerformances: {},
    isOverseas: incomingPlayer.isOverseas,
    price: incomingPlayer.price,
  };

  const updatedPlayers = players.map(p =>
    p.gender === gender && p.slotIndex === slotIndex ? newPlayer : p
  );

  return { updatedPlayers, bankedDelta };
}

/**
 * Apply a position swap: swap slotIndex between two players of the same gender.
 * Both players' pointsAtJoining are reset to their current points so future
 * contributions start fresh from the new multiplier.
 * Returns a new players array (no mutation) + the banked points delta.
 */
export function applyTennisPositionSwap(params: {
  players: SquadPlayer[];
  gender: 'men' | 'women';
  slotA: number;
  slotB: number;
  multipliers: number[];
}): { updatedPlayers: SquadPlayer[]; bankedDelta: number } {
  const { players, gender, slotA, slotB, multipliers } = params;

  const playerA = players.find(p => p.gender === gender && p.slotIndex === slotA);
  const playerB = players.find(p => p.gender === gender && p.slotIndex === slotB);

  const { totalBanked } = playerA && playerB
    ? bankTennisPositionSwap(playerA, playerB, multipliers)
    : { totalBanked: 0 };

  const updatedPlayers = players.map(p => {
    if (p.gender === gender && p.slotIndex === slotA) {
      return { ...p, slotIndex: slotB, pointsAtJoining: p.points };
    }
    if (p.gender === gender && p.slotIndex === slotB) {
      return { ...p, slotIndex: slotA, pointsAtJoining: p.points };
    }
    return p;
  });

  return { updatedPlayers, bankedDelta: totalBanked };
}

/**
 * Validate a player swap against budget and duplicate constraints.
 * Gender filtering is enforced by the UI (only the correct pool is shown).
 */
export function validateTennisPlayerSwap(params: {
  players: SquadPlayer[];
  gender: 'men' | 'women';
  slotIndex: number;
  incomingPlayerId: string;
  incomingPrice: number;
  budget: number;
}): { valid: boolean; error?: string } {
  const { players, gender, slotIndex, incomingPlayerId, incomingPrice, budget } = params;

  // Cannot pick a player already in the squad (in any slot)
  const alreadyInSquad = players.some(p => p.playerId === incomingPlayerId);
  if (alreadyInSquad) {
    return { valid: false, error: 'This player is already in your squad.' };
  }

  // Budget check: remove outgoing player's price, add incoming player's price
  const outgoing = players.find(p => p.gender === gender && p.slotIndex === slotIndex);
  const outgoingPrice = outgoing?.price ?? 0;
  const currentSpend = players.reduce((sum, p) => sum + (p.price ?? 0), 0);
  const newSpend = currentSpend - outgoingPrice + incomingPrice;

  if (budget > 0 && newSpend > budget) {
    const over = (newSpend - budget).toFixed(1);
    return { valid: false, error: `Over budget by ${over}cr. Free up credits first.` };
  }

  return { valid: true };
}

/**
 * Reverse a player swap performed by a user. Removes `playerInId` from the
 * given slot and restores the outgoing player using their *current* pool
 * snapshot (so they start earning from now). The caller is expected to
 * subtract `bankedAmount` from the squad's running bankedPoints — this
 * function returns it as a negative `bankedDelta` for convenience.
 */
export function reverseTennisPlayerSwap(params: {
  players: SquadPlayer[];
  gender: 'men' | 'women';
  slotIndex: number;
  playerInId: string;
  outgoingPoolEntry: PlayerPoolEntry;
  bankedAmount: number;
}): { updatedPlayers: SquadPlayer[]; bankedDelta: number } {
  const { players, gender, slotIndex, playerInId, outgoingPoolEntry, bankedAmount } = params;

  const incomingAtSlot = players.find(
    p => p.gender === gender && p.slotIndex === slotIndex && p.playerId === playerInId
  );
  if (!incomingAtSlot) {
    throw new Error(
      `Cannot reverse player swap: player ${playerInId} not found at ${gender} slot ${slotIndex}.`
    );
  }

  const restored: SquadPlayer = {
    playerId: outgoingPoolEntry.playerId,
    playerName: outgoingPoolEntry.name,
    team: outgoingPoolEntry.team,
    role: gender,
    gender,
    points: outgoingPoolEntry.points,
    pointsAtJoining: outgoingPoolEntry.points,
    slotIndex,
    matchPerformances: {},
    isOverseas: outgoingPoolEntry.isOverseas,
    price: outgoingPoolEntry.price,
  };

  const updatedPlayers = players.map(p =>
    p.gender === gender && p.slotIndex === slotIndex && p.playerId === playerInId
      ? restored
      : p
  );

  return { updatedPlayers, bankedDelta: bankedAmount === 0 ? 0 : -bankedAmount };
}

/**
 * Reverse a position swap performed by a user. Sends the player currently at
 * `slotB` back to `slotA` and vice versa. `pointsAtJoining` is intentionally
 * left as-is — the original pre-swap value isn't captured anywhere, and
 * future growth should accrue under the now-restored multiplier from this
 * moment forward.
 */
export function reverseTennisPositionSwap(params: {
  players: SquadPlayer[];
  gender: 'men' | 'women';
  slotA: number;
  slotB: number;
  bankedAmount: number;
}): { updatedPlayers: SquadPlayer[]; bankedDelta: number } {
  const { players, gender, slotA, slotB, bankedAmount } = params;

  const atSlotA = players.find(p => p.gender === gender && p.slotIndex === slotA);
  const atSlotB = players.find(p => p.gender === gender && p.slotIndex === slotB);
  if (!atSlotA || !atSlotB) {
    throw new Error(
      `Cannot reverse position swap: missing player at ${gender} slot ${atSlotA ? slotB : slotA}.`
    );
  }

  const updatedPlayers = players.map(p => {
    if (p.gender === gender && p.slotIndex === slotA) return { ...p, slotIndex: slotB };
    if (p.gender === gender && p.slotIndex === slotB) return { ...p, slotIndex: slotA };
    return p;
  });

  return { updatedPlayers, bankedDelta: bankedAmount === 0 ? 0 : -bankedAmount };
}

/**
 * Validate a position swap: must be different slots, both players must exist.
 */
export function validateTennisPositionSwap(params: {
  players: SquadPlayer[];
  gender: 'men' | 'women';
  slotA: number;
  slotB: number;
}): { valid: boolean; error?: string } {
  const { players, gender, slotA, slotB } = params;

  if (slotA === slotB) {
    return { valid: false, error: 'Choose two different slots to swap.' };
  }

  const playerA = players.find(p => p.gender === gender && p.slotIndex === slotA);
  if (!playerA) {
    return { valid: false, error: `No player found in slot ${slotA + 1}.` };
  }

  const playerB = players.find(p => p.gender === gender && p.slotIndex === slotB);
  if (!playerB) {
    return { valid: false, error: `No player found in slot ${slotB + 1}.` };
  }

  return { valid: true };
}

/**
 * Live preview of the squad's budget after a hypothetical player swap.
 *
 * Used by the Review Transfer button to decide enable/disable. Both
 * `currentSpend` and the outgoing player's price are looked up against the
 * pool as a fallback for legacy squads whose `SquadPlayer.price` was never
 * persisted — without that fallback, the outgoing slot would contribute 0cr
 * and the preview would falsely report the swap as over budget.
 */
export function computeTennisTransferPreviewBudget(params: {
  squadPlayers: SquadPlayer[];
  tennisPool: PlayerPoolEntry[];
  outgoingSlot: { gender: 'men' | 'women'; slotIndex: number };
  incomingPlayer: PlayerPoolEntry;
}): { currentSpend: number; previewBudget: number } {
  const { squadPlayers, tennisPool, outgoingSlot, incomingPlayer } = params;

  const priceFor = (playerId: string, stored: number | undefined): number => {
    if (stored !== undefined) return stored;
    return tennisPool.find(x => x.playerId === playerId)?.price ?? 0;
  };

  const currentSpend = squadPlayers.reduce(
    (sum, p) => sum + priceFor(p.playerId, p.price),
    0,
  );

  const outgoing = squadPlayers.find(
    p => p.gender === outgoingSlot.gender && p.slotIndex === outgoingSlot.slotIndex,
  );
  const outgoingPrice = outgoing ? priceFor(outgoing.playerId, outgoing.price) : 0;
  const incomingPrice = incomingPlayer.price ?? 0;

  return {
    currentSpend,
    previewBudget: currentSpend - outgoingPrice + incomingPrice,
  };
}
